// doctor-frontend/src/components/profile/ProfileForm.jsx
import React, { useEffect, useMemo, useState } from 'react';
import http from '../../services/http';

const CENTRES = ['CRHA Keru', 'CHRA Dhawa', 'CHRA Dundhara', 'UHTC Pratap Nagar'];

async function uploadFile(file, endpoint = '/api/uploads') {
  if (!file) throw new Error('No file selected');

  // Backend expects field name "image".
  const fd = new FormData();
  fd.append('image', file);
  // Use shared http client so baseURL/tenant prefix/auth are consistent.
  const resp = await http.post(endpoint, fd);
  const data = resp?.data || {};

  const rawUrl = data.url || data.imageUrl || data.path || data.fileUrl || '';
  if (!rawUrl) throw new Error('Upload succeeded but no URL was returned');

  return toAbsoluteMediaUrl(rawUrl);
}

function getApiBaseForMedia() {
  const raw = (process.env.REACT_APP_API_BASE || '').trim().replace(/\/+$/, '');
  if (raw && raw.startsWith('/')) return '';

  try {
    const { hostname, port, protocol } = window.location || {};
    const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
    if (isLocalHost && String(port) === '3000') {
      return `${protocol}//${hostname}:5000`;
    }
  } catch {}

  return raw || '';
}

function toAbsoluteMediaUrl(maybeRelativeUrl) {
  const u = String(maybeRelativeUrl || '').trim();
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;

  const base = getApiBaseForMedia();
  if (!base) return u;

  if (u.startsWith('/')) return `${base}${u}`;
  return `${base}/${u}`;
}


function readLoggedInUser() {
  try { return JSON.parse(localStorage.getItem('loggedInUser') || 'null'); } catch { return null; }
}
const isAdminOrFaculty = () => {
  const u = readLoggedInUser();
  const role = String(u?.role || u?.userType || u?.user?.role || '').toLowerCase();
  return /admin|faculty|hod|head|dean/.test(role);
};

export default function ProfileForm({ initial, onSave, saving, uploadEndpoint = '/api/uploads', id, compact }) {
  const [form, setForm] = useState(() => ({
    displayName: '',
    designation: '',
    departments: ['CMFM'],
    centres: [],
    qualifications: [],
    registrationNumber: '',
    experienceYears: 0,
    emailAlt: '',
    mobileAlt: '',
    officeLocation: '',
    workingHours: '',
    languagePrefs: ['en', 'hi'],
    dashboardLayout: 'grid-v2',
    areasOfInterest: [],   // authoritative array, built on submit
    photoURL: '',
    signatureURL: '',
    roleLevel: 0,
    mentorUserId: undefined,
    superviseeUserIds: [],
    permissions: [],
    goalsShortTerm: '',
    goalsLongTerm: '',
    ...(initial || {}),
  }));

  // Buffer string that preserves spaces while typing
  const [areasText, setAreasText] = useState(() => {
    const a = (initial?.areasOfInterest ?? []).join
      ? initial.areasOfInterest.join(', ')
      : String(initial?.areasOfInterest || '');
    return a;
  });

  useEffect(() => {
    if (initial) {
      setForm((prev) => ({ ...prev, ...initial }));
      const a = (initial?.areasOfInterest ?? []).join
        ? initial.areasOfInterest.join(', ')
        : String(initial?.areasOfInterest || '');
      setAreasText(a);
    }
  }, [initial]);

  const canSave = useMemo(() => !!(form.displayName || form.designation), [form.displayName, form.designation]);
  const onChange = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const adminOrFaculty = isAdminOrFaculty();
  const designationLower = String(form.designation || '').toLowerCase();
  const isIntern = designationLower.includes('intern');

  const grid = compact ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'grid grid-cols-1 md:grid-cols-2 gap-4';

  // Build final payload only when submitting (preserves spaces during typing)
  function buildPayload() {
    const out = { ...form };
    out.experienceYears = Number(out.experienceYears || 0);
    if (out.roleLevel === '' || out.roleLevel == null) delete out.roleLevel;
    else out.roleLevel = Number(out.roleLevel);

    // areas from text → array (allow phrases with spaces)
    out.areasOfInterest = String(areasText || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    // supervisees csv → array
    if (typeof out.superviseeUserIds === 'string') {
      out.superviseeUserIds = out.superviseeUserIds
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }

    // Ensure media URLs are absolute so they don't resolve to the frontend host
    if (out.photoURL) out.photoURL = toAbsoluteMediaUrl(out.photoURL);
    if (out.signatureURL) out.signatureURL = toAbsoluteMediaUrl(out.signatureURL);

    // Non-admin users cannot change hierarchy
    if (!adminOrFaculty) {
      delete out.roleLevel;
      delete out.superviseeUserIds;
      delete out.permissions;
      if (!adminOrFaculty) delete out.mentorUserId;
    }
    return out;
  }

  return (
    <form
      id={id}
      onSubmit={(e) => { e.preventDefault(); if (!canSave || saving) return; onSave(buildPayload()); }}
      className="space-y-4"
    >
      <div className="rounded-2xl shadow-md border border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-5">
        <div className={grid}>
        <div className="md:col-span-2">
          <div className="text-base font-semibold">Profile basics</div>
          <div className="text-xs opacity-70 mt-0.5">Core identity details used across the CMFM portal.</div>
        </div>

        {/* Identity */}
        <div>
          <label className="block text-sm mb-1">Display Name / नाम</label>
          <input className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700"
                 value={form.displayName || ''} onChange={(e) => onChange('displayName', e.target.value)} placeholder="Dr. A. B." />
        </div>
        <div>
          <label className="block text-sm mb-1">Designation / पद</label>
          <input className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700"
                 value={form.designation || ''} onChange={(e) => onChange('designation', e.target.value)}
                 placeholder="Intern / JR / SR / Faculty / Admin / DEO / MO-IC" />
        </div>

        <div className="md:col-span-2 mt-2 pt-4 border-t border-neutral-200 dark:border-neutral-800">
          <div className="text-sm font-semibold opacity-90">Work location</div>
          <div className="text-xs opacity-70 mt-0.5">Department and centre assignment (drives roster and portal access).</div>
        </div>

        {/* Org */}
        <div>
          <label className="block text-sm mb-1">Departments</label>
          <input className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700"
                 value={(form.departments || []).join(', ')}
                 onChange={(e) => onChange('departments', (e.target.value || '').split(',').map(s=>s.trim()).filter(Boolean))}
                 placeholder="CMFM, ..." />
        </div>
        <div>
          <label className="block text-sm mb-1">Centres</label>
          <select multiple className="w-full border rounded-md p-2 h-28" value={form.centres || []}
                  onChange={(e) => onChange('centres', Array.from(e.target.selectedOptions).map((o) => o.value))}>
            {CENTRES.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
          <div className="text-xs opacity-70 mt-1">Hold Ctrl/Cmd to select multiple.</div>
        </div>

        <div className="md:col-span-2 mt-2 pt-4 border-t border-neutral-200 dark:border-neutral-800">
          <div className="text-sm font-semibold opacity-90">Credentials</div>
          <div className="text-xs opacity-70 mt-0.5">Qualifications, registration and experience (for verification and reporting).</div>
        </div>

        {/* Credentials */}
        <div>
          <label className="block text-sm mb-1">Qualifications</label>
          <input className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700"
                 value={(form.qualifications || []).join(', ')}
                 onChange={(e) => onChange('qualifications', (e.target.value || '').split(',').map(s=>s.trim()).filter(Boolean))}
                 placeholder="MBBS, MD CMFM" />
        </div>
        <div>
          <label className="block text-sm mb-1">Registration Number</label>
          <input className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700" value={form.registrationNumber || ''}
                 onChange={(e) => onChange('registrationNumber', e.target.value)} />
        </div>
        <div>
          <label className="block text-sm mb-1">Experience (years)</label>
          <input type="number" min={0} max={80} className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700"
                 value={form.experienceYears ?? 0} onChange={(e) => onChange('experienceYears', Number(e.target.value || 0))} />
        </div>

        <div className="md:col-span-2 mt-2 pt-4 border-t border-neutral-200 dark:border-neutral-800">
          <div className="text-sm font-semibold opacity-90">Contact & availability</div>
          <div className="text-xs opacity-70 mt-0.5">Optional alternate contact and working hours.</div>
        </div>

        {/* Contact */}
        <div>
          <label className="block text-sm mb-1">Alternate Email</label>
          <input className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700" value={form.emailAlt || ''} onChange={(e) => onChange('emailAlt', e.target.value)} />
        </div>
        <div>
          <label className="block text-sm mb-1">Alternate Mobile</label>
          <input className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700" value={form.mobileAlt || ''} onChange={(e) => onChange('mobileAlt', e.target.value)} />
        </div>
        <div>
          <label className="block text-sm mb-1">Office Location</label>
          <input className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700" value={form.officeLocation || ''} onChange={(e) => onChange('officeLocation', e.target.value)} />
        </div>
        <div>
          <label className="block text-sm mb-1">Working Hours</label>
          <input className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700" value={form.workingHours || ''} onChange={(e) => onChange('workingHours', e.target.value)} placeholder="09:00–17:00, Mon–Fri" />
        </div>

        {/* Prefs */}
        <div>
          <label className="block text-sm mb-1">Languages (comma-separated, e.g. en, hi)</label>
          <input className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700"
                 value={(form.languagePrefs || []).join(', ')}
                 onChange={(e) => onChange('languagePrefs', (e.target.value || '').split(',').map(s=>s.trim()).filter(Boolean))} />
        </div>
        <div>
          <label className="block text-sm mb-1">Dashboard Layout</label>
          <input className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700" value={form.dashboardLayout || ''} onChange={(e) => onChange('dashboardLayout', e.target.value)} placeholder="grid-v2" />
        </div>

        <div className="md:col-span-2 mt-2 pt-4 border-t border-neutral-200 dark:border-neutral-800">
          <div className="text-sm font-semibold opacity-90">Media</div>
          <div className="text-xs opacity-70 mt-0.5">Profile photo and signature (optional).</div>
        </div>

        {/* Uploads */}
        <div>
          <label className="block text-sm mb-1">Photo URL</label>
          <div className="flex items-center gap-2">
            <input className="flex-1 border rounded-md p-2" value={form.photoURL || ''}
                   onChange={(e) => onChange('photoURL', e.target.value)} placeholder="/uploads/yourphoto.png" />
            <label className="text-xs px-2 py-2 rounded-md bg-neutral-100 dark:bg-neutral-800 border cursor-pointer">
              Upload
              <input type="file" accept="image/*" className="hidden"
                     onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return;
                       try { const url = await uploadFile(f, uploadEndpoint); onChange('photoURL', url); }
                       catch (err) { alert(err.message || 'Photo upload failed'); } }} />
            </label>
          </div>
        </div>
        <div>
          <label className="block text-sm mb-1">Signature URL</label>
          <div className="flex items-center gap-2">
            <input className="flex-1 border rounded-md p-2" value={form.signatureURL || ''}
                   onChange={(e) => onChange('signatureURL', e.target.value)} placeholder="/uploads/signature.png" />
            <label className="text-xs px-2 py-2 rounded-md bg-neutral-100 dark:bg-neutral-800 border cursor-pointer">
              Upload
              <input type="file" accept="image/*" className="hidden"
                     onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return;
                       try { const url = await uploadFile(f, uploadEndpoint); onChange('signatureURL', url); }
                       catch (err) { alert(err.message || 'Signature upload failed'); } }} />
            </label>
          </div>
        </div>

        {/* Hierarchy (admin/faculty) */}
        <div>
          <label className="block text-sm mb-1">Role Level (sorting)</label>
          <input
            type="number"
            className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700"
            value={form.roleLevel ?? 0}
            onChange={(e) => onChange('roleLevel', Number(e.target.value || 0))}
            disabled={!adminOrFaculty}
            placeholder="0 = highest seniority"
          />
          {!adminOrFaculty && <div className="text-xs opacity-70 mt-1">Admin/Faculty set this.</div>}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm mb-1">Supervisee User Ids (comma-separated)</label>
          <input
            className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700"
            value={Array.isArray(form.superviseeUserIds) ? form.superviseeUserIds.join(', ') : (form.superviseeUserIds || '')}
            onChange={(e) => onChange('superviseeUserIds', e.target.value)}
            disabled={!adminOrFaculty}
            placeholder="66f7c1..., 66f7c2..., 66f7c3..."
          />
          {!adminOrFaculty && <div className="text-xs opacity-70 mt-1">Admin/Faculty manage supervisees.</div>}
        </div>

        {/* Intern mentor */}
        {isIntern && (
          <div>
            <label className="block text-sm mb-1">Mentor User Id</label>
            <input
              className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700"
              value={form.mentorUserId || ''}
              onChange={(e) => onChange('mentorUserId', e.target.value)}
              disabled={!adminOrFaculty}
              placeholder="e.g., 66f7c1f3a12b..."
            />
            {!adminOrFaculty && <div className="text-xs opacity-70 mt-1">Ask admin/faculty to set mentor.</div>}
          </div>
        )}

        <div className="md:col-span-2 mt-2 pt-4 border-t border-neutral-200 dark:border-neutral-800">
          <div className="text-sm font-semibold opacity-90">Goals</div>
          <div className="text-xs opacity-70 mt-0.5">Short-term and long-term goals for mentoring and performance review.</div>
        </div>

        {/* Goals */}
        <div className="md:col-span-2">
          <label className="block text-sm mb-1">Short-term Goal</label>
          <textarea rows={2} className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700"
            value={form.goalsShortTerm || ''} onChange={(e) => onChange('goalsShortTerm', e.target.value)}
            placeholder="What do you want to achieve in the next 3–6 months?"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm mb-1">Long-term Goal</label>
          <textarea rows={2} className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700"
            value={form.goalsLongTerm || ''} onChange={(e) => onChange('goalsLongTerm', e.target.value)}
            placeholder="Where do you want to be in 1–3 years?"
          />
        </div>

        <div className="md:col-span-2 mt-2 pt-4 border-t border-neutral-200 dark:border-neutral-800">
          <div className="text-sm font-semibold opacity-90">About</div>
          <div className="text-xs opacity-70 mt-0.5">Professional bio and areas of interest.</div>
        </div>

        {/* Bio & Interests */}
        <div className="md:col-span-2">
          <label className="block text-sm mb-1">Areas of Interest (comma-separated — spaces allowed)</label>
          <input
            className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700"
            value={areasText}
            onChange={(e) => setAreasText(e.target.value)}
            placeholder="Primary care, Occupational health, ..."
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm mb-1">Bio</label>
          <textarea rows={3} className="w-full border rounded-md p-2 bg-white dark:bg-neutral-900 dark:border-neutral-700" value={form.bio || ''}
            onChange={(e) => onChange('bio', e.target.value)} placeholder="Short professional bio…" />
        </div>
        </div>
      </div>

      {/* Buttons are provided by the parent header */}
      <div className="hidden">
        <button type="submit" disabled={!canSave || saving}>
          {saving ? 'Saving…' : 'Save Profile'}
        </button>
      </div>
    </form>
  );
}
